import React from "react"
import type { AiSubject } from "../../Session/plan-lesson/ai-lesson-planning/aiLessonPlanningTypes"
import { SUBJECT_META, heatCellColors, skillBand } from "../learnerReportModel"
import type { SkillBand, SkillRow } from "../learnerReportModel"
import { BasketBadge, EmptyNote, ExpandButton, ScoreLegend, WhenBadge } from "../reportUi"
import type { ReportView } from "./reportView"

type BandFilter = "all" | "Weak" | "In progress" | "Passed" | "Not started"

const FILTERS: BandFilter[] = ["all", "Weak", "In progress", "Passed", "Not started"]

function matchesFilter(band: SkillBand, filter: BandFilter): boolean {
  if (filter === "all") return true
  if (filter === "In progress") return band === "In progress" || band === "Good"
  return band === filter
}

const SkillsTab: React.FC<{ view: ReportView; subject: AiSubject }> = ({ view, subject }) => {
  const sk = view.skills[subject]
  const meta = SUBJECT_META[subject]
  const [open, setOpen] = React.useState<string>("")
  const [filter, setFilter] = React.useState<BandFilter>("all")
  const withFilter = subject === "english"

  React.useEffect(() => {
    setOpen("")
    setFilter("all")
  }, [subject])

  if (sk.total === 0) {
    return (
      <section className="sr-card sr-section">
        <h2 className="sr-h2">Current-year {meta.label} skill status</h2>
        <EmptyNote>
          No {meta.label} skills found for this student yet. Skills appear once the student has a year
          level or has attempted {meta.label} activities.
        </EmptyNote>
      </section>
    )
  }

  const filterCount = (f: BandFilter) => sk.skills.filter(s => matchesFilter(s.band.label, f)).length
  const visible = (s: SkillRow) => !withFilter || matchesFilter(s.band.label, filter)
  const strands = sk.strands
    .map(g => ({ ...g, skills: g.skills.filter(visible) }))
    .filter(g => g.skills.length > 0)
  const weak = sk.counts.Weak

  return (
    <>
      <section aria-label={`${meta.label} status`} className="sr-card sr-status-card">
        <div className="sr-status-left">
          {sk.currentYear !== null ? (
            <span className="sr-year-badge" style={{ background: meta.solid }}>Year {sk.currentYear}</span>
          ) : null}
          <h2 className="sr-h2" style={{ fontSize: 20 }}>Current-year {meta.label} skill status</h2>
          {sk.lockLabel ? <span className="sr-lock-chip">{sk.lockLabel}</span> : null}
        </div>
        <div className="sr-status-stats">
          <div className="sr-stat">
            <span className="sr-stat-label">Passed</span>
            <span className="sr-stat-value">
              {sk.passed} <span className="sr-stat-of">/ {sk.total}</span>
            </span>
          </div>
          <div className="sr-stat">
            <span className="sr-stat-label">Average</span>
            <span className="sr-stat-value">{sk.avg === null ? "–" : sk.avg.toFixed(1)}</span>
          </div>
          {subject === "math" ? (
            <div className="sr-stat">
              <span className="sr-stat-label">Close to passing</span>
              <span className="sr-stat-value">{sk.close}</span>
            </div>
          ) : (
            <div className="sr-stat">
              <span className="sr-stat-label">Weak</span>
              <span className="sr-stat-value" style={{ color: "#B42335" }}>{weak}</span>
            </div>
          )}
        </div>
      </section>

      <div className="sr-skills-toolbar">
        {withFilter ? (
          <div role="group" aria-label="Filter skills" className="sr-pill-group sr-no-print">
            {FILTERS.map(f => (
              <button
                key={f}
                type="button"
                className="sr-pill"
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
              >
                {f === "all" ? "All" : f} ({f === "all" ? sk.total : filterCount(f)})
              </button>
            ))}
          </div>
        ) : (
          <span />
        )}
        <ScoreLegend />
      </div>

      {sk.heatYears.length > 0 ? (
        <section aria-label="Score by year" className="sr-card sr-section" style={{ gap: 14 }}>
          <div className="sr-stack-4">
            <h2 className="sr-h2">Score by year</h2>
            <span className="sr-muted">
              Read across a row to see the year each skill starts to slip. That is the year to go back to.
            </span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <div
              className="sr-heat"
              style={{
                gridTemplateColumns: `minmax(180px, 1.4fr) repeat(${sk.heatYears.length}, minmax(72px, 1fr)) minmax(150px, 1fr)`,
              }}
            >
              <span className="sr-heat-head">Skill</span>
              {sk.heatYears.map(y => (
                <span
                  key={y}
                  className={y === sk.currentYear ? "sr-heat-head sr-heat-head-now" : "sr-heat-head sr-heat-center"}
                >
                  Y{y}
                  {y === sk.currentYear ? " · now" : ""}
                </span>
              ))}
              <span className="sr-heat-head" style={{ paddingLeft: 8 }}>Starts to slip</span>
              {sk.skills.filter(visible).map(s => (
                <React.Fragment key={s.name}>
                  <span className="sr-heat-name">{s.name}</span>
                  {s.heat.map(c => {
                    const [bg, fg] = heatCellColors(c.score)
                    return (
                      <span
                        key={c.year}
                        className="sr-heat-cell"
                        style={{ background: bg, color: fg }}
                        title={`Y${c.year}: ${c.score === null ? "not attempted" : c.score.toFixed(1)}`}
                      >
                        {c.score === null ? "–" : c.score.toFixed(1)}
                      </span>
                    )
                  })}
                  <span className="sr-heat-slip" style={{
                      color: s.slip === "Secure" ? "#1E6E40" : s.slip === "Not attempted" ? "#8C93A3" : "#A8243A",
                    }}>
                    {s.slip}
                  </span>
                </React.Fragment>
              ))}
            </div>
          </div>
          <span className="sr-muted-12">Cells show the score in each year's content. – means not attempted.</span>
        </section>
      ) : null}

      {strands.length === 0 ? <EmptyNote>No skills in this group.</EmptyNote> : null}

      {strands.map(g => (
        <section key={g.name} className="sr-stack-10">
          <div className="sr-head-baseline">
            <h3 className="sr-h3 sr-strand-title">
              <span className="sr-strand-bar" style={{ background: g.color }} />
              {g.name}
            </h3>
            <span className="sr-muted-12">
              {g.skills.length} {g.skills.length === 1 ? "skill" : "skills"}
            </span>
          </div>
          <div className="sr-card sr-skill-list">
            {g.skills.map(s => {
              const isOpen = open === s.name
              return (
                <div key={s.name} className="sr-skill-item">
                  <div className="sr-skill-row">
                    <div className="sr-skill-name">
                      <span style={{ fontSize: 15, fontWeight: 600 }}>{s.name}</span>
                      <span className="sr-muted-12">{s.range}</span>
                    </div>
                    <div className="sr-skill-bar">
                      <div style={{ width: `${s.score === null ? 0 : s.score * 10}%`, background: s.band.solid }} />
                    </div>
                    <span className="sr-skill-score" style={{ color: s.band.fg }}>
                      {s.score === null ? "–" : s.score.toFixed(1)}
                    </span>
                    <span className="sr-skill-chip" style={{ background: s.band.bg, color: s.band.fg }}>
                      {s.band.label}
                    </span>
                    <ExpandButton
                      open={isOpen}
                      onClick={() => setOpen(isOpen ? "" : s.name)}
                      label={`Show detail for ${s.name}`}
                    />
                  </div>
                  {isOpen ? (
                    <div className="sr-skill-detail">
                      <div className="sr-stack-8">
                        <span className="sr-field-label">Year-by-year score record</span>
                        <div className="sr-year-record">
                          {s.years.map((y, i) => {
                            const cur = i === s.years.length - 1
                            return (
                              <div key={y.year} className="sr-year-cell" style={{ background: cur ? "#EEF1FB" : "#FFFFFF" }}>
                                <span className="sr-muted-12">Y{y.year}</span>
                                <span className="sr-mono" style={{ fontSize: 14, fontWeight: 700, color: skillBand(y.score).fg }}>
                                  {y.score === null ? "–" : y.score.toFixed(1)}
                                </span>
                                <span style={{ fontSize: 11, color: "#3B5BCC", fontWeight: 600 }}>{cur ? "Current" : ""}</span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                      <div className="sr-flex-row" style={{ gap: 16 }}>
                        <div className="sr-stack-8" style={{ flex: "1 1 320px" }}>
                          <span className="sr-field-label">Activities this year</span>
                          {s.acts.length ? (
                            <div className="sr-stack-6">
                              {s.acts.map((a, i) => (
                                <div key={i} className="sr-act-row">
                                  <span style={{ flex: 1 }}>{a.name}</span>
                                  <span className="sr-text-sub">{a.meta}</span>
                                  <b className="sr-mono">{a.score}</b>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="sr-text-sub">Not attempted yet this year.</span>
                          )}
                        </div>
                        <div className="sr-stack-8" style={{ flex: "1 1 280px" }}>
                          <span className="sr-field-label">Recommended next</span>
                          {s.next ? (
                            <div className="sr-next-card">
                              <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                <WhenBadge when={s.next.when} />
                                <BasketBadge basket={s.next.basket} />
                              </span>
                              <span style={{ fontSize: 14, fontWeight: 600 }}>{s.next.title}</span>
                              <span className="sr-muted-12">{s.next.meta}</span>
                            </div>
                          ) : (
                            <span className="sr-text-sub">No recommendation for this skill right now.</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </>
  )
}

export default SkillsTab
